
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MdExpandLess, MdExpandMore, MdLogout, MdSearch } from "react-icons/md";
import NAV from "../../Utils/MenuData";
import {
	permissionHas,
	hasAnyPermission,
	isSuperAdminUser,
	resolveUserPermissionCodes,
} from "../../Utils/Permissions";
import brandLogo from "../../Assets/Images/aegis-icons.png";
import "../../Styles/SidebarLayout.css";
import { useAuth } from "../../Context/AuthContext";


const findActiveKeyByPath = (pathname) => {
	for (const item of NAV) {
		if (item.children?.length) {
			for (const child of item.children) {
				const childRoute = child.page || child.path;

				if (childRoute && pathname.startsWith(childRoute)) {
					return {
						key: `${item.label}/${child.label}`,
						parent: item.label,
					};
				}
			}
		}

		const itemRoute = item.page || item.path;
		if (itemRoute && pathname.startsWith(itemRoute)) {
			return { key: item.label, parent: null };
		}
	}

	return { key: null, parent: null };
};

export default function SideBar({ onNavigate, onLogout }) {
	const navigate = useNavigate();
	const location = useLocation();
	const [query, setQuery] = useState("");
	const [activeMenu, setActiveMenu] = useState(() => {
		const { key } = findActiveKeyByPath(location.pathname);
		return key || null;
	});
	const [expanded, setExpanded] = useState(() => {
		const { parent } = findActiveKeyByPath(location.pathname);
		return parent ? { [parent]: true } : {};
	});
	const { decodedToken, permissions, menuItems } = useAuth();
	const isSuperAdmin = isSuperAdminUser(decodedToken);
	const userPermissionCodes = useMemo(
		() => resolveUserPermissionCodes({ permissions, decodedToken }),
		[permissions, decodedToken]
	);

	useEffect(() => {
		const { key, parent } = findActiveKeyByPath(location.pathname);
		if (key) {
			setActiveMenu(key);
			if (parent) {
				setExpanded((prev) => ({ ...prev, [parent]: true }));
			}
		}
	}, [location.pathname]);

	const fillRouteParams = (pagePath) => {
		if (!pagePath) return pagePath;
		let result = pagePath;

		if (result.includes(":id")) {
			let paramId = null;
			if (pagePath.includes("/users/view")) {
				paramId = decodedToken?.user_id || decodedToken?.sub || decodedToken?.id || null;
			} else if (pagePath.includes("/providers/view")) {
				paramId = decodedToken?.provider_id || decodedToken?.ProviderId || decodedToken?.providerId || null;
			} else if (pagePath.includes("/staffs/view")) {
				paramId = decodedToken?.staff_id || decodedToken?.StaffId || decodedToken?.staffId || null;
			} else if (pagePath.includes("/organizations/view")) {
				paramId = decodedToken?.organization_id || decodedToken?.OrganizationId || decodedToken?.organizationId || null;
			} else if (pagePath.includes("/clients/view")) {
				paramId = decodedToken?.client_id || decodedToken?.ClientId || decodedToken?.clientId || null;
			}
			if (!paramId) {
				return null;
			}
			result = result.replace(":id", String(paramId));
		}
		if (result.includes(":accountId")) {
			const accountId = decodedToken?.account_id || decodedToken?.AccountId || null;
			if (!accountId) return null;
			result = result.replace(":accountId", String(accountId));
		}
		if (result.includes(":organization_id")) {
			const orgId = decodedToken?.organization_id || decodedToken?.OrganizationId || decodedToken?.organizationId || null;
			if (!orgId) return null;
			result = result.replace(":organization_id", String(orgId));
		}
		if (result.includes(":facility_name")) {
			result = result.replace(":facility_name", "first");
		}
		if (result.includes(":provider_name")) {
			result = result.replace(":provider_name", "first");
		}
		return result;
	};

	const resolveTargetPage = (item) => {
		if (!item.page) return item.path;

		const itemPermission = item.permission;
		const hasMainPermission = !itemPermission || isSuperAdmin || permissionHas(userPermissionCodes, itemPermission, decodedToken);
		if (hasMainPermission) {
			const filledMain = fillRouteParams(item.page);
			if (filledMain) return filledMain;
		}

		if (!item.fallbackRoutes?.length) {
			return null;
		}

		const fallbackPriority = (fallback) => {
			const permissionCode = fallback?.permission || "";
			const pagePath = fallback?.page || "";

			if (pagePath.includes("/view")) return 10;
			if (permissionCode.includes("_VIEW")) return 20;
			if (permissionCode.includes("_EDIT")) return 30;
			if (permissionCode.includes("_CREATE")) return 40;
			return 50;
		};

		const sortedFallbacks = item.fallbackRoutes
			.slice()
			.sort((a, b) => fallbackPriority(a) - fallbackPriority(b));

		for (const fallback of sortedFallbacks) {
			if (permissionHas(userPermissionCodes, fallback.permission, decodedToken)) {
				const resolved = fillRouteParams(fallback.page);
				if (resolved) return resolved;
			}
		}

		return null;
	};

	const hasAccessibleRoute = (item) => {
		if (item.page) {
			return Boolean(resolveTargetPage(item));
		}
		if (item.children?.length) {
			return item.children.some((child) => Boolean(resolveTargetPage(child)));
		}
		return false;
	};

	const assignedServicesSet = useMemo(() => {
		if (!Array.isArray(menuItems) || menuItems.length === 0) {
			return null;
		}
		const set = new Set();
		menuItems.forEach((m) => {
			if (typeof m === "string" || typeof m === "number") {
				set.add(String(m).toLowerCase().trim());
			} else if (typeof m === "object" && m !== null) {
				[
					m.name,
					m.Name,
					m.label,
					m.Label,
					m.service_name,
					m.ServiceName,
					m.code,
					m.Code,
					m.id,
					m.Id,
					m.service_id,
					m.ServiceId,
				]
					.filter(Boolean)
					.forEach((val) => set.add(String(val).toLowerCase().trim()));
			}
		});
		return set;
	}, [menuItems]);

	const isServiceAssigned = (item) => {
		if (isSuperAdmin || !assignedServicesSet) return true;
		const idMatch = item.id ? assignedServicesSet.has(String(item.id).toLowerCase()) : false;
		const labelMatch = item.label ? assignedServicesSet.has(item.label.toLowerCase().trim()) : false;
		const nameMatch = item.name ? assignedServicesSet.has(item.name.toLowerCase().trim()) : false;
		return idMatch || labelMatch || nameMatch;
	};

	const hasListOrViewOrEditPermission = (item) => {
		if (isSuperAdmin) return true;
		const itemPermissions = item.permissions || (item.permission ? [item.permission] : []);
		if (!itemPermissions.length) return true;

		// Check if user has at least one of LIST, VIEW, or EDIT permission
		const listOrViewOrEditPermissions = itemPermissions.filter((p) =>
			p.endsWith("_LIST") ||
			p.endsWith("_VIEW") ||
			p.endsWith("_EDIT") ||
			p.includes("_LIST") ||
			p.includes("_VIEW") ||
			p.includes("_EDIT")
		);

		const permissionsToCheck = listOrViewOrEditPermissions.length > 0 ? listOrViewOrEditPermissions : itemPermissions;
		return hasAnyPermission(userPermissionCodes, permissionsToCheck, decodedToken);
	};

	const filteredMenu = useMemo(() => {
		const term = query.trim().toLowerCase();
		let menu = NAV;

		const shouldShowItem = (item) => {
			if (isSuperAdmin) return true;
			if (!isServiceAssigned(item)) return false;
			return hasListOrViewOrEditPermission(item);
		};

		const applyFilter = (items) =>
			items
				.map((item) => {
					const isParentAssigned = isServiceAssigned(item);
					if (!isSuperAdmin && !isParentAssigned) {
						return null;
					}

					if (item.children?.length) {
						const children = item.children.filter((child) =>
							isSuperAdmin || hasListOrViewOrEditPermission(child) || Boolean(resolveTargetPage(child))
						);
						if (!children?.length) return null;
						return { ...item, children };
					}

					if (!shouldShowItem(item) && !hasAccessibleRoute(item)) {
						return null;
					}

					return item;
				})
				.filter(Boolean);

		menu = applyFilter(menu);

		if (!term) {
			return menu;
		}

		return menu
			.map((item) => {
				const labelMatches = item.label.toLowerCase().includes(term);
				const children = item.children?.filter((child) =>
					child.label.toLowerCase().includes(term)
				);

				if (labelMatches) {
					return item;
				}

				if (children?.length) {
					return { ...item, children };
				}

				return null;
			})
			.filter(Boolean);
	}, [query, userPermissionCodes, isSuperAdmin, assignedServicesSet]);

	const selectMenu = (key, item) => {
		setActiveMenu(key);

		const targetPage = item ? resolveTargetPage(item) : null;
		if (targetPage) {
			navigate(targetPage);
		}

		if (onNavigate) {
			onNavigate(targetPage);
		}
	};

	const toggleExpanded = (label) => {
		setExpanded((prev) => ({
			...prev,
			[label]: !prev[label],
		}));
	};

	const handleLogoutClick = () => {
		if (onLogout) {
			onLogout();
		}

		if (onNavigate) {
			onNavigate();
		}
	};

	return (
		<div className="hc-sidebar h-100 d-flex flex-column">
			<div className="hc-sidebar-brand d-flex align-items-center mb-2">
				<div className="hc-brand-mark overflow-hidden">
					<img src={brandLogo} alt="Aegis 360" className="hc-brand-logo w-100 h-100 object-fit-cover d-block" />
				</div>
				<div className="hc-brand-copy">
					<h2 className="m-0">AEGIS 360</h2>
					<p>Admin Console</p>
				</div>
			</div>

			<div className="hc-sidebar-search d-flex align-items-center gap-2">
				<MdSearch size={18} />
				<input
					type="text"
					placeholder="Search menu"
					value={query}
					onChange={(event) => setQuery(event.target.value)}
					aria-label="Search menu"
					className="border-0 w-100"
				/>
			</div>

			<nav className="hc-sidebar-nav mt-2 overflow-y-auto" aria-label="Main">
				{filteredMenu.map((item) => {
					const hasChildren = Boolean(item.children?.length);
					const isOpen = Boolean(expanded[item.label]);
					const parentActive = activeMenu === item.label;

					return (
						<div className="hc-menu-block mb-2" key={item.label}>
							<button
								type="button"
								className={`hc-menu-item w-100 border-0 d-flex align-items-center justify-content-between text-start ${parentActive ? "active" : ""}`}
								onClick={() => {
									if (hasChildren) {
										toggleExpanded(item.label);
									} else {
										selectMenu(item.label, item);
									}
								}}
							>
								<span className="hc-menu-left d-flex align-items-center">
									<span className="hc-menu-icon d-inline-flex align-items-center justify-content-center">{item.icon}</span>
									<span>{item.label}</span>
								</span>
								{hasChildren ? (
									<span className="hc-menu-expand-icon">
										{isOpen ? <MdExpandLess size={18} /> : <MdExpandMore size={18} />}
									</span>
								) : null}
							</button>

							{hasChildren && isOpen ? (
								<div className="hc-submenu-wrap mt-1">
									{item.children.map((child) => {
										const key = `${item.label}/${child.label}`;
										const childActive = activeMenu === key;

										return (
											<button
												key={key}
												type="button"
												className={`hc-submenu-item w-100 border-0 d-flex align-items-center justify-content-start text-start ${childActive ? "active" : ""}`}
												onClick={() => selectMenu(key, child)}
											>
												<span className="hc-menu-icon d-inline-flex align-items-center justify-content-center">{child.icon}</span>
												<span>{child.label}</span>
											</button>
										);
									})}
								</div>
							) : null}
						</div>
					);
				})}
			</nav>

			<div className="hc-sidebar-footer">
				<button type="button" className="hc-logout-btn w-100 border-0 d-flex align-items-center justify-content-center gap-2" onClick={handleLogoutClick}>
					<span className="hc-menu-icon d-inline-flex align-items-center justify-content-center">
						<MdLogout size={18} />
					</span>
					<span>Logout</span>
				</button>
			</div>
		</div>
	);
}
